// Päikeseparkide ülevaade — peamine töölaud.
//
// Kuvab:
//   - Header: pealkiri, LIVE indikaator, viimase uuenduse aeg, Uuenda nupp
//   - Totals grid: 5 kogusummade kaarti (tootmine, võrk, aku SOC, tarbimine, energia)
//   - Parks grid: 15 parki (paigaldatud + paigaldamata + offline)
//
// Andmed: GET /api/parks (poll iga 5s) — useParks hook
// Eeskuju: parks_dashboard.py mall

import { useCallback, useState } from 'react';
import { useParks } from '../hooks/useParks';
import { useAuth } from '../contexts/AuthContext';
import { TotalsGrid, ParkCard } from '../components/Parks';
import { fmtUpdatedAt } from '../utils/format';

export function DashboardPage() {
  const { data, loading, error, refresh, isStale } = useParks();
  const { isSuperadmin, tenantId, viewTenant, viewTenantName } = useAuth();

  // Pealkiri kliendi järgi — Semuehitus OÜ pargid on "Wesene" nime all
  const titleTenant = isSuperadmin ? viewTenant : tenantId;
  const title = titleTenant === 'semuehitus' ? 'Wesene päikeseparkide ülevaade'
    : (isSuperadmin && viewTenant) ? `${viewTenantName || viewTenant} — päikeseparkide ülevaade`
    : 'Päikeseparkide ülevaade';
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refresh();
    // visuaalne spin natuke kauem näha
    setTimeout(() => setRefreshing(false), 350);
  }, [refresh]);

  // Sorteerimine: paigaldatud (online → offline) → paigaldamata
  const sortedParks = (data?.parks || []).slice().sort((a, b) => {
    const ai = a.name ? (a.online ? 0 : 1) : 2;
    const bi = b.name ? (b.online ? 0 : 1) : 2;
    if (ai !== bi) return ai - bi;
    return (a.slot || 0) - (b.slot || 0);
  });

  const live = !error && !isStale;

  return (
    <div className="parks-wrap">
      {/* Header */}
      <div className="parks-header">
        <div className="brand">
          <div className="brand-mark">
            <span>☀</span>
          </div>
          <div>
            <h1>{title}</h1>
          </div>
        </div>
        <div className="parks-header-right">
          <span className={`live${live ? '' : ' stale'}${data?.paused ? ' stale' : ''}`}>
            <span className="dot" />
            <span>{data?.paused ? 'LAHTI ÜHENDATUD' : (live ? 'LIVE' : 'OFFLINE')}</span>
          </span>
          <span className="time">{fmtUpdatedAt(data?.updated)}</span>

          {/* Lahti/sisse ühendamise toggle — peatab KÕIGI parkide Modbus liikluse → superadmin */}
          {isSuperadmin && <button
            type="button"
            className="parks-btn"
            onClick={async () => {
              const newPaused = !data?.paused;
              try {
                const url = newPaused ? '/api/parks/pause' : '/api/parks/resume';
                await fetch(url, { method: 'POST' });
                refresh();
              } catch (e) {
                console.error('Toggle failed:', e);
              }
            }}
            style={data?.paused ? { borderColor: '#15803d', color: '#15803d' } : { borderColor: '#b91c1c', color: '#b91c1c' }}
            title={data?.paused ? 'Taasta Modbus ühendus seadmetega' : 'Lõpeta kõik Modbus lugemine ja kirjutamine'}
          >
            {data?.paused ? '▶ Ühenda' : '⏸ Lahti ühenda'}
          </button>}

          <button
            type="button"
            className={`parks-btn${refreshing ? ' spin' : ''}`}
            onClick={handleRefresh}
            disabled={data?.paused}
            style={data?.paused ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <svg>
              <use href="#i-refresh" />
            </svg>
            Uuenda
          </button>
        </div>
      </div>

      {/* Totals */}
      <TotalsGrid total={data?.total} loading={loading && !data} />

      {/* Parkide grid */}
      <div className="parks-grid">
        {loading && !data ? (
          // Skeleton — 6 tühja kaarti
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton-card" style={{ height: 220 }} />
          ))
        ) : sortedParks.length === 0 ? (
          <div className="text-[13px] text-zinc-500 py-6">Selle kliendi all pole veel ühtegi parki.</div>
        ) : (
          sortedParks.map((park) => (
            <ParkCard key={park.host} park={park} />
          ))
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
